import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService, Funcionario, RegistroPonto, RegistroPontoAlteracao } from '../../api.service';

export type LinhaExportacaoPonto = {
  chave: string;
  funcionarioId: string;
  matricula: string;
  nome: string;
  dataIso: string;
  data: string;
  diaSemana: string;
  batidas: string[];
};

export type FormatoExportacao = 'csv' | 'txt' | 'pdf';

export type FiltroExportacaoPadrao = {
  dataInicial: string;
  dataFinal: string;
  formato: FormatoExportacao;
  departamentoSelecionado: string[];
};

export type ResultadoExportacao = {
  registros: RegistroPonto[];
  linhas: LinhaExportacaoPonto[];
  linhaEditando: string;
};

export type EstadoEdicaoExportacao = {
  linhaEditando: string;
  batidasEdicao: string[];
};

export type EstadoFiltroExportacao = {
  funcionarioSelecionado: string;
  departamentoSelecionado: string[];
  buscaFuncionario: string;
  buscaFuncionarioModal: string;
  dataInicial: string;
  dataFinal: string;
  formato: FormatoExportacao;
};

export type EstadoFuncionarioFiltroExportacao = {
  funcionarioSelecionado: string;
  buscaFuncionario: string;
  buscaFuncionarioModal: string;
};

@Injectable({ providedIn: 'root' })
export class ServicoExportacaoPonto {
  constructor(private readonly api: ApiService) {}

  filtroPadrao(): FiltroExportacaoPadrao {
    const hoje = new Date();
    return {
      dataInicial: this.dataParaIsoLocal(new Date(hoje.getFullYear(), hoje.getMonth(), 1)),
      dataFinal: this.dataParaIsoLocal(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)),
      formato: 'pdf',
      departamentoSelecionado: []
    };
  }

  estadoFiltroLimpo(): EstadoFiltroExportacao {
    const filtroPadrao = this.filtroPadrao();
    return {
      funcionarioSelecionado: '',
      buscaFuncionario: '',
      buscaFuncionarioModal: '',
      dataInicial: filtroPadrao.dataInicial,
      dataFinal: filtroPadrao.dataFinal,
      formato: filtroPadrao.formato,
      departamentoSelecionado: filtroPadrao.departamentoSelecionado
    };
  }

  estadoFuncionarioSelecionado(funcionario: Funcionario, rotulo: string): EstadoFuncionarioFiltroExportacao {
    return {
      funcionarioSelecionado: funcionario.id || '',
      buscaFuncionario: rotulo,
      buscaFuncionarioModal: ''
    };
  }

  estadoFuncionarioLimpo(): EstadoFuncionarioFiltroExportacao {
    return {
      funcionarioSelecionado: '',
      buscaFuncionario: '',
      buscaFuncionarioModal: ''
    };
  }

  listarRegistros(
    empregadorId: string,
    dataInicial: string,
    dataFinal: string,
    funcionarioId?: string,
    departamentoIds?: string[]
  ): Observable<RegistroPonto[]> {
    return this.api.listarRegistros(empregadorId, dataInicial, dataFinal, funcionarioId, departamentoIds);
  }

  pesquisarFolhaPonto(
    empregadorId: string,
    dataInicial: string,
    dataFinal: string,
    funcionarios: Funcionario[],
    funcionarioSelecionado: string,
    departamentoIds?: string[]
  ): Observable<ResultadoExportacao> {
    return this.listarRegistros(empregadorId, dataInicial, dataFinal, funcionarioSelecionado || undefined, departamentoIds).pipe(
      map(registros => ({
        registros,
        linhas: this.montarLinhas(registros, funcionarios, funcionarioSelecionado, dataInicial, dataFinal),
        linhaEditando: ''
      }))
    );
  }

  baixarArquivoPonto(
    empregadorId: string,
    dataInicial: string,
    dataFinal: string,
    formato: Exclude<FormatoExportacao, 'pdf'>,
    funcionarioId?: string,
    departamentoIds?: string[]
  ): Observable<Blob> {
    return this.api.baixarExportacao(empregadorId, dataInicial, dataFinal, formato, funcionarioId, departamentoIds);
  }

  formatoArquivoPonto(formato: FormatoExportacao): Exclude<FormatoExportacao, 'pdf'> {
    return formato === 'txt' ? 'txt' : 'csv';
  }

  baixarFolhaPdf(
    empregadorId: string,
    dataInicial: string,
    dataFinal: string,
    funcionarioId?: string,
    departamentoIds?: string[]
  ): Observable<Blob> {
    return this.api.baixarFolhasPontoPdf(empregadorId, dataInicial, dataFinal, funcionarioId, departamentoIds);
  }

  ajustarFolhaDia(funcionarioId: string, data: string, batidas: string[]): Observable<RegistroPonto[]> {
    return this.api.ajustarFolhaDia(funcionarioId, data, batidas);
  }

  listarAlteracoesFolha(empregadorId: string, funcionarioId: string, data: string): Observable<RegistroPontoAlteracao[]> {
    return this.api.listarAlteracoesFolha(empregadorId, funcionarioId, data);
  }

  mensagemErroHistoricoFolha() {
    return 'Nao foi possivel carregar o historico de alteracoes da folha.';
  }

  montarLinhas(
    registros: RegistroPonto[],
    funcionarios: Funcionario[],
    funcionarioSelecionado: string,
    dataInicial: string,
    dataFinal: string
  ) {
    const linhas = new Map<string, LinhaExportacaoPonto>();
    const funcionarioFiltrado = funcionarios.find(item => item.id === funcionarioSelecionado);

    if (funcionarioFiltrado) {
      for (const dataIso of this.datasPeriodoDesc(dataInicial, dataFinal)) {
        const chave = `${funcionarioFiltrado.id}-${dataIso}`;
        linhas.set(chave, this.criarLinhaExportacao(chave, funcionarioFiltrado, dataIso));
      }
    }

    const registrosOrdenados = [...registros].sort((a, b) =>
      new Date(a.dataHora).getTime() - new Date(b.dataHora).getTime()
    );

    for (const registro of registrosOrdenados) {
      const dataIso = this.dataIsoRegistro(registro.dataHora);
      const chave = `${registro.funcionario.id || registro.funcionario.matricula}-${dataIso}`;

      if (!linhas.has(chave)) {
        linhas.set(chave, this.criarLinhaExportacao(chave, registro.funcionario, dataIso));
      }

      const linha = linhas.get(chave);
      if (linha) {
        const indiceBatida = this.indiceBatida(registro, linha);
        if (indiceBatida >= 0 && indiceBatida < 4 && !linha.batidas[indiceBatida]) {
          linha.batidas[indiceBatida] = this.formatarHoraMinuto(registro.dataHora);
        }
      }
    }

    return Array.from(linhas.values()).sort((a, b) =>
      b.dataIso.localeCompare(a.dataIso) || a.nome.localeCompare(b.nome)
    );
  }

  nomeArquivoPonto(dataInicial: string, dataFinal: string, formato: string) {
    return `ponto-${dataInicial}-${dataFinal}.${formato}`;
  }

  nomeArquivoFolhaPdf(dataInicial: string, dataFinal: string) {
    return `folha-ponto-${dataInicial}-${dataFinal}.pdf`;
  }

  copiarBatidas(linha: LinhaExportacaoPonto) {
    return [...linha.batidas];
  }

  limparBatidasEdicao() {
    return ['', '', '', ''];
  }

  resultadoVazio(): ResultadoExportacao {
    return {
      registros: [],
      linhas: [],
      linhaEditando: ''
    };
  }

  iniciarEdicao(linha: LinhaExportacaoPonto): EstadoEdicaoExportacao {
    return {
      linhaEditando: linha.chave,
      batidasEdicao: this.copiarBatidas(linha)
    };
  }

  cancelarEdicao(): EstadoEdicaoExportacao {
    return {
      linhaEditando: '',
      batidasEdicao: this.limparBatidasEdicao()
    };
  }

  deveEntrarEmEdicao(linhaEditando: string, linha: LinhaExportacaoPonto) {
    return linhaEditando !== linha.chave;
  }

  mensagemArquivoGerado() {
    return 'Arquivo gerado no layout MATRICULA/NOME/DATA/entradas e saidas.';
  }

  mensagemFolhaPdfGerada() {
    return 'Folha de ponto em PDF gerada.';
  }

  mensagemFuncionarioObrigatorioPesquisa() {
    return 'Selecione um funcionario para pesquisar a folha de ponto.';
  }

  mensagemEmpregadorObrigatorioExportacao() {
    return 'Selecione um empregador para exportar.';
  }

  mensagemFuncionarioObrigatorioPdf() {
    return 'Selecione um funcionario ou deixe vazio para gerar todos em PDF.';
  }

  mensagemErroExportarArquivo() {
    return 'Erro ao exportar arquivo.';
  }

  mensagemErroAbrirPdf() {
    return 'Nao foi possivel abrir o PDF em nova aba. Verifique o bloqueador de pop-ups do navegador.';
  }

  mensagemFolhaAjustada() {
    return 'Folha de ponto ajustada.';
  }

  mensagemErroCarregarRegistros() {
    return 'Erro ao carregar registros.';
  }

  baixarBlob(blob: Blob, nomeArquivo: string) {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = nomeArquivo;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  abrirPdfEmNovaAba(blob: Blob, aba: Window) {
    const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    aba.location.href = url;
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  }

  private datasPeriodoDesc(dataInicial: string, dataFinal: string) {
    const datas: string[] = [];
    const inicio = new Date(`${dataInicial}T00:00:00`);
    const fim = new Date(`${dataFinal}T00:00:00`);
    if (Number.isNaN(inicio.getTime()) || Number.isNaN(fim.getTime()) || inicio > fim) {
      return datas;
    }

    for (const data = new Date(fim); data >= inicio; data.setDate(data.getDate() - 1)) {
      datas.push(this.dataParaIsoLocal(data));
    }
    return datas;
  }

  private dataIsoRegistro(dataHora: string) {
    return this.dataParaIsoLocal(new Date(dataHora));
  }

  private dataParaIsoLocal(data: Date) {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  private dataIsoParaBr(dataIso: string) {
    const [ano, mes, dia] = dataIso.split('-');
    return `${dia}/${mes}/${ano}`;
  }

  private criarLinhaExportacao(chave: string, funcionario: Funcionario, dataIso: string): LinhaExportacaoPonto {
    return {
      chave,
      funcionarioId: funcionario.id || '',
      matricula: funcionario.matricula,
      nome: funcionario.nome,
      dataIso,
      data: this.dataIsoParaBr(dataIso),
      diaSemana: this.nomeDiaSemana(dataIso),
      batidas: this.limparBatidasEdicao()
    };
  }

  private nomeDiaSemana(dataIso: string) {
    const data = new Date(`${dataIso}T00:00:00`);
    return this.capitalizarPrimeiraLetra(data.toLocaleDateString('pt-BR', { weekday: 'long' }));
  }

  private capitalizarPrimeiraLetra(valor: string) {
    return valor ? valor.charAt(0).toUpperCase() + valor.slice(1) : '';
  }

  private indiceBatida(registro: RegistroPonto, linha: LinhaExportacaoPonto) {
    if (registro.numeroBatida && registro.numeroBatida >= 1 && registro.numeroBatida <= 4) {
      return registro.numeroBatida - 1;
    }

    return linha.batidas.findIndex(valor => !valor);
  }

  private formatarHoraMinuto(dataHora: string) {
    return new Date(dataHora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
}
